<template>
  <UModal :dismissible="false" class="w-[300px]">
    <template #header>
      <div class="flex flex-col items-center gap-4 justify-center w-full py-5">
        <h2 class="text-2xl font-bold text-center">{{ labels?.title }}</h2>
        <div
          class="flex items-center bg-linear-to-t from-muted to-accented p-5 rounded-full"
        >
          <div
            class="flex items-center bg-default p-5 rounded-full border border-accented"
          >
            <UIcon
              name="material-symbols:person-shield"
              class="size-10 text-accented"
            />
          </div>
        </div>
      </div>
    </template>
    <template #body>
      <div class="px-4 py-2 mx-auto" :class="{ '!animate-shake': shakeError }">
        <UForm
          ref="form"
          :schema="schema"
          :state="state"
          @submit="onSubmit"
          class="flex flex-col items-center gap-2"
        >
          <UFormField
            :label="emailField?.label"
            required
            name="email"
            help="&nbsp;"
            class="w-full"
          >
            <UInput
              ref="emailRef"
              v-model="state.email"
              :name="emailField?.name"
              class="w-full"
              required
              icon="i-lucide-at-sign"
              size="lg"
              @keydown.enter="onSubmit"
              :placeholder="emailField?.placeholder"
            />
          </UFormField>

          <UFormField
            v-if="!forgotPassword"
            :label="passwordField?.label"
            required
            name="password"
            class="w-full"
          >
            <UInput
              ref="passwordRef"
              v-model="state.password"
              icon="i-lucide-lock"
              :name="passwordField?.name"
              class="w-full"
              :type="show ? 'text' : 'password'"
              required
              size="lg"
              @keydown.enter="onSubmit"
              :placeholder="passwordField?.placeholder"
            >
              <template #trailing>
                <UButton
                  color="neutral"
                  variant="link"
                  size="sm"
                  :icon="show ? 'i-lucide-eye-off' : 'i-lucide-eye'"
                  :aria-label="show ? labels.hidePassword : labels.showPassword"
                  :aria-pressed="show"
                  aria-controls="password"
                  @click="show = !show"
                />
              </template>
            </UInput>
          </UFormField>
          <UButton
            v-else
            :label="labels?.resetPassword"
            icon="lucide:refresh-ccw-dot"
            color="neutral"
            :disabled="!isEmailValid"
            variant="soft"
            :active="isEmailValid"
            active-color="success"
            :loading="loading"
            @click="onResetPasswordClick"
            class="mt-7"
          />
        </UForm>
      </div>
    </template>
    <template #footer>
      <UButton
        v-if="!forgotPassword"
        :label="labels?.forgotPassword"
        variant="ghost"
        color="error"
        @click="onForgotPasswordClick"
      />
      <UButton
        v-else
        :label="labels?.backToLogin"
        variant="ghost"
        color="error"
        icon="lucide:arrow-left"
        @click="forgotPassword = false"
      />
      <UButton
        v-if="!forgotPassword"
        :disabled="!isValid"
        :active="isValid"
        active-variant="solid"
        active-color="primary"
        variant="soft"
        color="neutral"
        @click="onSubmit"
        class="ml-auto"
        :loading="loading"
        :label="labels?.login"
      />
    </template>
  </UModal>
</template>

<script lang="ts" setup>
import * as z from "zod";

const { data: loginContent } = await useAsyncData(() =>
  queryCollection("content").path("/login").first()
);

const { data: emailField } = await useAsyncData("email-field", () =>
  queryCollection("fields").where("name", "=", "email").first()
);

const { data: passwordField } = await useAsyncData("password-field", () =>
  queryCollection("fields").where("name", "=", "password").first()
);

const labels: any = computed(() => loginContent.value?.meta || {});

const route = useRoute();

const forgotPassword = ref(false);

const formRef = useTemplateRef("form");
const emailRef = useTemplateRef("emailRef");

const emit = defineEmits(["success", "close"]);

const show = ref(false);

const schema = z.object({
  email: z.email(labels.invalidEmailAddress),
});

const state = reactive({
  email: (route.query?.email as string) || "",
  password: "",
});

// Computed property for overall validity
const isValid = computed(() => {
  const result = schema.safeParse(state);
  return result.success;
});

const isEmailValid = computed(() => {
  const result = schema.shape.email.safeParse(state.email);
  return result.success;
});

const passwordRef = useTemplateRef("passwordRef");

watch(passwordRef, (_passwordRef) => {
  if (_passwordRef && route.query?.email) {
    _passwordRef.inputRef?.focus();
  }
});

const { loggedIn, user, fetch: refreshSession, clear } = useUserSession();

const loading = ref(false);

const toast = useToast();

const shakeError = ref(false);

async function onSubmit() {
  if (!isValid.value) return;
  try {
    loading.value = true;
    await $fetch("/api/login", {
      method: "POST",
      body: state,
    });

    await refreshSession();

    emit("success");
  } catch (e) {
    shakeError.value = true;
    setTimeout(() => {
      shakeError.value = false;
    }, 750); // 0.25s × 3 iterations = 0.75s

    toast.add({
      title: "Login failed",
      description: labels.value.badCredentials,
      color: "error",
    });
  } finally {
    loading.value = false;
  }
}

const onForgotPasswordClick = async () => {
  forgotPassword.value = true;
  await nextTick();
  emailRef.value?.inputRef?.focus();
};

async function onResetPasswordClick() {
  loading.value = true;
  try {
    await $fetch("/api/reset-token/request", {
      method: "POST",
      body: state,
    });
    toast.add({
      title: "Success",
      description: labels.value.checkYourEmail,
      color: "success",
    });
  } catch (e) {
    toast.add({
      title: "Error",
      description: labels.value.failedToRequestResetToken,
      color: "error",
    });
  } finally {
    loading.value = false;
  }
}
</script>

<style></style>
